// src/core/auth.js — autenticación real contra Supabase Auth.
//
// FASE 4 · F1.2 (21-may-2026). Reemplaza al mock de sesión que vivía en este
// módulo (sub-fase 3.2) y al mock-login.js (archivado en /legacy/). Dos
// métodos de ingreso conviven: Google Sign-In y email + "clave futbolera".
//
// "Clave futbolera" es el password de Supabase Auth (auth.users) — solo el
// nombre de cara al usuario cambia. El campo usuarios.football_key_hash NO se
// usa (DT-E: eliminar del schema en v1.7).
//
// getCurrentUser() CONSERVA su firma exterior (CLAUDE.md §4): sigue siendo
// SÍNCRONA y devuelve un objeto-o-null con la misma forma que devolvía el
// mock. Para lograrlo se mantiene un cache de módulo (currentUserCache) que se
// hidrata async en initAuth() y se refresca en cada onAuthStateChange.
//
// Helpers legacy del mundo localStorage (getCurrentUserId, setCurrentUserId,
// findUserByEmail, findUserByGoogleId, createUser) se conservan intactos:
// grupos y memberships siguen en localStorage hasta F1.3 — mueren con esa fase.

import { supabase } from '../supabase.js'
import { normalizeEmail } from './db.js'
import { withTimeout, queryWithTimeout } from '../utils/timeout.js'
import { purgeSupabaseAuthStorage } from '../utils/supabase-storage.js'

// Auditoría boot-hang (5-jun-2026): tope para los awaits de auth al arranque.
// Si Supabase no responde, se trata como "sin sesión" para este boot en vez de
// colgar el primer render.
const AUTH_TIMEOUT_MS = 12000

// ¿El error de getSession indica un token DEFINITIVAMENTE inválido (corresponde
// desloguear) y no un bache transitorio (5xx/red/timeout, NO desloguear)?
// Supabase/GoTrue devuelve estos códigos/mensajes cuando el refresh token ya no
// sirve; un 5xx (status>=500) o error de red es transitorio.
export const esTokenInvalidoDefinitivo = (error) => {
  if (!error) return false
  const status = Number(error.status) || 0
  if (status >= 500) return false // bache de GoTrue → transitorio
  const code = String(error.code || '').toLowerCase()
  const msg = String(error.message || '').toLowerCase()
  return (
    code === 'refresh_token_not_found' ||
    code === 'invalid_grant' ||
    code === 'session_not_found' ||
    code === 'refresh_token_already_used' ||
    msg.includes('refresh token not found') ||
    msg.includes('invalid refresh token') ||
    msg.includes('invalid grant') ||
    msg.includes('already used')
  )
}

// ─── Cache de sesión (permite que getCurrentUser sea síncrona) ─────────────

let currentUserCache = null
const authCallbacks = new Set()

// Normaliza authUser (Supabase) + fila public.usuarios al shape que esperan
// los consumers (mismos campos que devolvía el mock + nativos del schema).
const buildUser = (authUser, row) => {
  if (!authUser) return null
  const meta = authUser.user_metadata || {}
  const nombreReal = row?.nombre_real || meta.full_name || meta.name || ''
  return {
    user_id: authUser.id,
    email: row?.email || authUser.email || '',
    email_verified: Boolean(authUser.email_confirmed_at || authUser.confirmed_at),
    real_name: nombreReal,          // alias legacy que consumen main.js / grupo.js
    nombre_real: nombreReal,        // nombre nativo de la columna del schema
    global_nickname: row?.global_nickname || meta.global_nickname || '',
    avatar_url: row?.avatar_url || meta.avatar_url || meta.picture || null,
    tipo: row?.tipo || 'nacional',
    auth_provider: row?.auth_provider || authUser.app_metadata?.provider || 'email',
    google_id: null,                // shape parity con el mock; Supabase no lo expone
    pais_origen: row?.pais_origen || null,
    bandera_pais: row?.bandera_pais || null,
    edad: row?.edad ?? null,
    sexo: row?.sexo || null,
    created_at: row?.created_at || authUser.created_at || null,
    updated_at: row?.updated_at || null,
  }
}

// Lee la sesión cacheada de localStorage (SYNC, instantáneo). Evita
// supabase.auth.getSession() en el boot: getSession se bloquea en el lock interno
// de GoTrue cuando el auto-refresh post-resume lo tiene tomado (evidencia Eruda:
// el ÚNICO error del cuelgue era "[auth] getSession timeout"). supabase guarda la
// sesión en `sb-<ref>-auth-token`. Válida = access_token presente y expires_at
// futuro. Corrupta/expirada/ausente → null (→ landing, sin colgar).
const leerSesionCacheada = () => {
  if (typeof localStorage === 'undefined') return null
  let key = null
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) { key = k; break }
    }
    if (!key) return null
    const raw = JSON.parse(localStorage.getItem(key))
    if (raw && raw.access_token && raw.expires_at && raw.expires_at * 1000 > Date.now()) return raw
    return null
  } catch (_) {
    // Token corrupto (JSON inválido): purga defensiva (audit 5-jun) para no
    // relear basura en cada boot. Un token sólo EXPIRADO no cae acá (parsea ok)
    // → se conserva para que supabase-js lo refresque en background.
    if (key) { try { purgeSupabaseAuthStorage() } catch (__) { /* ignore */ } }
    return null
  }
}

// Reconstruye el cache de sesión + la fila usuarios.
//   - session provista (evento onAuthStateChange) → se usa.
//   - session omitida (boot) → se LEE DE localStorage (sync). NUNCA getSession()
//     acá: se bloquea en el lock de GoTrue post-resume (causa raíz del cuelgue
//     ~12s). La validación/refresh real va en background (ver initAuth).
const refreshCache = async (session) => {
  const sess = session !== undefined ? session : leerSesionCacheada()
  const authUser = sess?.user || null
  if (!authUser) {
    currentUserCache = null
    return null
  }

  let row = null
  try {
    const { data, error } = await queryWithTimeout(
      (signal) => supabase.from('usuarios').select('*').eq('user_id', authUser.id).abortSignal(signal).maybeSingle(),
      AUTH_TIMEOUT_MS, 'usuarios',
    )
    if (error) console.error('[auth] lectura de public.usuarios falló:', error.message)
    else row = data
  } catch (err) {
    // Timeout/abort: seguimos con row=null (el perfil se completa con metadata
    // del authUser); el dato real se rehidrata en el próximo onAuthStateChange.
    console.error('[auth] usuarios timeout:', err?.message || err)
  }
  currentUserCache = buildUser(authUser, row)
  return currentUserCache
}

const notify = (event) => {
  authCallbacks.forEach((cb) => {
    try {
      cb(event, currentUserCache)
    } catch (err) {
      console.error('[auth] callback de onAuthStateChange falló:', err)
    }
  })
}

// ─── Inicialización: hidrata el cache y engancha el listener de Supabase ────

let authInitialized = false

// Llamada una vez en el bootstrap (src/main.js). Espera el estado de auth de
// Supabase antes de que la app decida qué pintar. Idempotente.
export const initAuth = async () => {
  // Boot: hidrata desde la sesión cacheada en localStorage (sync, sin getSession
  // → sin lock de GoTrue). + la fila usuarios (con timeout, rápida).
  await refreshCache()
  if (!authInitialized) {
    supabase.auth.onAuthStateChange((event, session) => {
      refreshCache(session)
        .then(() => notify(event))
        .catch((err) => console.error('[auth] refresh tras', event, 'falló:', err))
    })
    authInitialized = true
  }
  // Validar/refrescar el token en BACKGROUND (fire-and-forget). supabase-js maneja
  // el lock/refresh; si tarda no afecta el render (ya pintamos con la sesión
  // cacheada). En la 1ª request que dé 401 se refresca o se desloguea.
  setTimeout(() => { try { supabase.auth.getSession().catch(() => {}) } catch (_) { /* sin auth */ } }, 0)
  return currentUserCache
}

// ─── Métodos de ingreso ────────────────────────────────────────────────────

// Redirige el navegador a Google. Al volver, detectSessionInUrl (ver
// supabase.js) canjea el code y dispara onAuthStateChange.
export const signInWithGoogle = () =>
  supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  })

export const signInWithEmail = async (email, password) => {
  // DT-H3-resto: timeout — si Auth no responde, devolvemos {error} (login muestra
  // el mensaje + rehabilita el botón = reintentar) en vez de colgar "Procesando…".
  let result
  try {
    result = await withTimeout(supabase.auth.signInWithPassword({ email: normalizeEmail(email), password }), AUTH_TIMEOUT_MS, 'signIn')
  } catch (err) {
    return { data: { user: null, session: null }, error: { message: 'No pudimos conectar. Probá de nuevo.', code: 'timeout' } }
  }
  // Pre-hidratar el cache ANTES de devolver (espeja signUpWithEmail): cuando
  // login.js navega (applyIntent → hashchange → renderApp), currentUserCache ya
  // está listo → renderApp ve authed=true y pinta el destino directo, sin el
  // flash de renderLogin entre el signIn y el evento SIGNED_IN asíncrono.
  if (result.data?.session) await refreshCache(result.data.session)
  return result
}

// Alta con email. El trigger on_auth_user_created crea la fila en
// public.usuarios. OJO (DT-F): ese trigger arma global_nickname como
// <local-part>-<hex> e ignora el apodo elegido — por eso, si el alta deja
// sesión activa, persistimos a mano nombre_real + global_nickname elegidos.
export const signUpWithEmail = async (email, password, nombre_real, global_nickname) => {
  // DT-H3-resto: timeout en el alta (Auth). En timeout → {error} y la UI reintenta.
  let result
  try {
    result = await withTimeout(supabase.auth.signUp({
      email: normalizeEmail(email),
      password,
      options: { data: { full_name: nombre_real, nombre_real, global_nickname } },
    }), AUTH_TIMEOUT_MS, 'signUp')
  } catch (err) {
    return { data: null, error: { message: 'No pudimos conectar. Probá de nuevo.', code: 'timeout' } }
  }
  if (result.error) return result

  // BUG #3 / DT-AY — La protección anti-enumeración de Supabase Auth NO
  // devuelve error al registrar un email ya existente: devuelve un "user"
  // obfuscado (identities:[] y sin session). Sin esta normalización el alta
  // parecía exitosa y la UI mostraba "te enviamos un correo de confirmación"
  // — feedback falso. Lo convertimos en un error explícito y uniforme.
  if (isObfuscatedExistingUser(result.data)) {
    return {
      data: result.data,
      error: { message: 'User already registered', code: 'user_already_exists' },
    }
  }

  if (result.data?.session) {
    // best-effort + timeout: no bloquear el alta si la persistencia del perfil cuelga.
    try { await withTimeout(applyChosenProfile(result.data.user.id, nombre_real, global_nickname), AUTH_TIMEOUT_MS, 'applyProfile') }
    catch (err) { console.error('[auth] applyChosenProfile timeout:', err?.message || err) }
    await refreshCache(result.data.session)
  }
  return result
}

// Persiste el nombre real y el apodo elegidos en el alta. global_nickname es
// UNIQUE en el schema: si el apodo ya está tomado, se conserva el generado
// por el trigger (no es bloqueante para el alta).
const applyChosenProfile = async (userId, nombre_real, global_nickname) => {
  const full = await supabase
    .from('usuarios')
    .update({ nombre_real, global_nickname })
    .eq('user_id', userId)
  if (!full.error) return
  if (full.error.code === '23505') {
    console.warn('[auth] global_nickname ya en uso; se conserva el generado por el trigger')
    const partial = await supabase
      .from('usuarios')
      .update({ nombre_real })
      .eq('user_id', userId)
    if (partial.error) console.error('[auth] no se pudo persistir nombre_real:', partial.error.message)
  } else {
    console.error('[auth] no se pudo persistir el perfil elegido:', full.error.message)
  }
}

// Detecta la respuesta obfuscada de signUp para un email ya registrado: user
// presente, identities array vacío y sin session. Es el método que documenta
// Supabase para distinguir un alta nueva de un email ya tomado (un alta nueva
// real trae al menos una identity).
const isObfuscatedExistingUser = (data) => {
  const u = data?.user
  return Boolean(u) && !data?.session &&
    Array.isArray(u.identities) && u.identities.length === 0
}

// Logout USER-INITIATED. Antes era `() => supabase.auth.signOut()` (scope global
// = POST de revoke al server) SIN timeout → en celu con conexión muerta colgaba
// y el location.replace del caller nunca corría (causa raíz del freeze de logout).
// Ahora: 1) signOut LOCAL (limpia sesión sin red, instantáneo, dispara SIGNED_OUT);
// 2) revoke global best-effort, FIRE-AND-FORGET (no se espera; igual el fetch
// global lo aborta a los 15s). La UI nunca depende del revoke → location.replace
// SIEMPRE corre.
export const signOut = async () => {
  // 1) Cleanup LOCAL primero (sin red, instantáneo, dispara SIGNED_OUT).
  try { await supabase.auth.signOut({ scope: 'local' }) } catch (_) { /* best-effort */ }
  // 2) Purga TRANSACCIONAL: aunque el revoke global cuelgue, el estado local queda
  //    100% limpio (borra cualquier clave sb-* que el signOut local no haya tocado
  //    → evita el loop de boot corrompido tras logout fallido).
  purgeSupabaseAuthStorage()
  // 3) Revoke global best-effort, FIRE-AND-FORGET (el fetch global lo aborta a 15s).
  try { Promise.resolve(supabase.auth.signOut({ scope: 'global' })).catch(() => {}) } catch (_) { /* fire-and-forget */ }
  return { error: null }
}

// Dispara el correo de recuperación de clave (Supabase Auth). El enlace del
// correo vuelve a la app con type=recovery en la URL. OJO (DT-BC): el landing
// que cierra el flujo — fijar la clave nueva — todavía no está construido.
export const requestPasswordReset = async (email) => {
  try {
    return await withTimeout(
      supabase.auth.resetPasswordForEmail(normalizeEmail(email), { redirectTo: window.location.origin }),
      AUTH_TIMEOUT_MS, 'resetPwd')
  } catch (err) {
    return { data: null, error: { message: 'No pudimos conectar. Probá de nuevo.', code: 'timeout' } }
  }
}

// ─── Lectura de sesión ──────────────────────────────────────────────────────

// SÍNCRONA — firma exterior preservada (CLAUDE.md §4). Lee del cache hidratado
// por initAuth(); devuelve null si no hay sesión.
//
// F1.3.a: ésta es la vía CANÓNICA de lectura de la entidad `usuarios`. El
// cache se hidrata desde public.usuarios en Postgres (ver refreshCache, arriba)
// — la entidad usuarios ya no vive en localStorage. Cobertura de tests:
// tests/unit/auth-postgres.test.js.
export const getCurrentUser = () => currentUserCache

// Wrapper de supabase.auth.onAuthStateChange. El callback recibe (event, user)
// donde user ya es el objeto normalizado (o null). Devuelve un unsubscribe.
export const onAuthStateChange = (callback) => {
  authCallbacks.add(callback)
  return () => authCallbacks.delete(callback)
}

// ─── getCurrentUserId — id del usuario actual ──────────────────────────────
// F1.3.b: deriva del cache de getCurrentUser() (UUID de Supabase Auth). Ya no
// lee localStorage. Lo consume rituales.js (protegido) hasta F1.3.d.
//
// DT-X cerrada: el código muerto del mock de auth — findUserByEmail,
// findUserByGoogleId, createUser, setCurrentUserId — se eliminó en F1.3.b
// junto con sus ramas `!currentUser` en main.js y grupo.js.

export const getCurrentUserId = () => getCurrentUser()?.user_id ?? null
